import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useEffect } from "react";

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

export const DateFormate = (dateStr: string, lang: string = "en-US") => {
  const formattedDate = new Intl.DateTimeFormat(lang, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(dateStr));
  return formattedDate;
};

export const formatTimeTimeZone = (timeString: string, timeZone: string) => {
  if (!timeString) return "غير متوفر";

  const date = new Date(timeString);
  return new Intl.DateTimeFormat("ar-us", {
    timeZone: timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

export const formatTime = (timeString: string) => {
  if (!timeString) return "غير متوفر";

  const date = new Date(timeString);
  return new Intl.DateTimeFormat("ar-us", {
    timeZone: "Africa/Cairo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export const convertToCairoTime = (timeString: string, timezone: string) => {
  // Create a dayjs object with today's date and the UTC time string
  const today = dayjs().format("YYYY-MM-DD");
  // Parse as UTC then convert to Cairo time
  return dayjs
    .utc(`${today} ${timeString}`, "YYYY-MM-DD HH:mm")
    .tz(timezone)
    .format("HH:mm");
};

export const convertToUTC = (timeString: string, timezone: string) => {
  // Create a dayjs object with today's date and the time string
  const today = dayjs().format("YYYY-MM-DD");
  // Parse as Cairo time then convert to UTC
  return dayjs(`${today} ${timeString}`, "YYYY-MM-DD HH:mm")
    .tz(timezone)
    .utc()
    .format("HH:mm");
};

export function useBlockAllCopy() {
  useEffect(() => {
    // 1) Prevent any copy/cut/contextmenu/selectstart at capture phase
    const kill = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    document.addEventListener("copy", kill, true);
    document.addEventListener("cut", kill, true);
    document.addEventListener("contextmenu", kill, true);
    document.addEventListener("selectstart", kill, true);

    // 2) Prevent keyboard shortcuts (Ctrl/Cmd + C/X)
    const killKeys = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["c", "x", "C", "X"].includes(e.key)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener("keydown", killKeys, true);

    // 3) Override execCommand
    try {
      document.execCommand = () => false;
    } catch (err) {
      // cannot override? ignore
    }

    // 4) Stub out navigator.clipboard
    if (navigator.clipboard) {
      navigator.clipboard.writeText = () =>
        Promise.reject(new Error("Copy disabled"));
      navigator.clipboard.readText = () =>
        Promise.reject(new Error("Copy disabled"));
    }

    return () => {
      document.removeEventListener("copy", kill, true);
      document.removeEventListener("cut", kill, true);
      document.removeEventListener("contextmenu", kill, true);
      document.removeEventListener("selectstart", kill, true);
      document.removeEventListener("keydown", killKeys, true);
    };
  }, []);
}

export const parseField = (field: any, language: string) => {
  if (!field) return "";
  try {
    const parsed = JSON.parse(field?.en);
    return parsed[language] || "";
  } catch {
    return field[language] || "";
  }
};

export const formatPrice = (price: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(price);
